
import { useEditor, EditorContent, useEditorState } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'

import IndentExtension from './TipTaxIndentExtension.tsx';
import './TipTap.css';

import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import IconBold from "@mui/icons-material/FormatBold";
import IconItalic from "@mui/icons-material/FormatItalic";
import IconStrike from "@mui/icons-material/FormatStrikethrough";
import IconListBullet from "@mui/icons-material/FormatListBulleted";
import IconListOrdered from "@mui/icons-material/FormatListNumbered";
import IconAlignLeft from "@mui/icons-material/FormatAlignLeft";
import IconAlignCenter from "@mui/icons-material/FormatAlignCenter";
import IconAlignRight from "@mui/icons-material/FormatAlignRight";
import IconAlignJustify from "@mui/icons-material/FormatAlignJustify";
import IconIndentIncrease from "@mui/icons-material/FormatIndentIncrease";
import IconIndentDescrease from "@mui/icons-material/FormatIndentDecrease";
import Paper from '@mui/material/Paper';

import { forwardRef, useImperativeHandle } from 'react'

export interface TiptapEditorRef {
  getHTML: () => string
  getJSON: () => any
  setContent: (content: string) => void
}



const Tiptap = forwardRef<TiptapEditorRef, {}>((props, ref) => {
  const colorActive = 'blue'
  const colorInactive = 'black'

  useImperativeHandle(ref, () => ({
    getHTML: () => editor?.getHTML() || '',
    getJSON: () => editor?.getJSON() || {},
    setContent: (content: string) => {
    editor?.commands.setContent(content)
  },
  }))


  const editor = useEditor({
    extensions: [StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      IndentExtension,
    ],
    content: ``,
  })


  function MenuBar({ editor }: { editor: Editor }) {
    // Read the current editor's state, and re-render the component when it changes
    const editorState = useEditorState({
      editor,
      selector: ctx => {
        return {
          isBold: ctx.editor.isActive('bold') ?? false,
          canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
          isItalic: ctx.editor.isActive('italic') ?? false,
          canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
          isStrike: ctx.editor.isActive('strike') ?? false,
          canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
          isCode: ctx.editor.isActive('code') ?? false,
          canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
          canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
          isParagraph: ctx.editor.isActive('paragraph') ?? false,
          isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
          isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
          isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
          isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
          isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
          isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
          isBulletList: ctx.editor.isActive('bulletList') ?? false,
          isOrderedList: ctx.editor.isActive('orderedList') ?? false,
          isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
          isBlockquote: ctx.editor.isActive('blockquote') ?? false,
          canUndo: ctx.editor.can().chain().undo().run() ?? false,
          canRedo: ctx.editor.can().chain().redo().run() ?? false,
          isLinkActive: ctx.editor.isActive('link') ?? false,
        }
      },
    })

    return (
      <div className="control-group">
        <div className="button-group">
        </div>
        <Paper elevation={3} >
          <Toolbar style={{  minHeight: 40, height:20 }}>
            <IconButton sx={{ p: 0, m: 0, minWidth: 0 }}
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={!editorState.canBold}>
              <IconBold fontSize="small" sx={{ color: editorState.isBold ? colorActive : colorInactive }} />
            </IconButton>
            <IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={!editorState.canItalic}>
              <IconItalic fontSize="small" sx={{ color: editorState.isItalic ? colorActive : colorInactive }} />
            </IconButton>
            <IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().toggleStrike().run()}
              disabled={!editorState.canStrike}>
              <IconStrike fontSize="small" sx={{ color: editorState.isStrike ? colorActive : colorInactive }} />
            </IconButton>
            &nbsp;&nbsp;<IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().toggleBulletList().run()}>
              <IconListBullet fontSize="small" sx={{ color: editorState.isBulletList ? colorActive : colorInactive }} />
            </IconButton>

            <IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              <IconListOrdered fontSize="small" sx={{ color: editorState.isOrderedList ? colorActive : colorInactive }} />
            </IconButton>

            &nbsp; &nbsp; <IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().setTextAlign('left').run()}>
              <IconAlignLeft fontSize="small" />
            </IconButton>
            <IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().setTextAlign('center').run()}>
              <IconAlignCenter fontSize="small" />
            </IconButton>
            <IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().setTextAlign('right').run()}>
              <IconAlignRight fontSize="small" />
            </IconButton>

            <IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}>
              <IconAlignJustify fontSize="small" />
            </IconButton>

            &nbsp;&nbsp;<IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().indent().run()}>
              <IconIndentIncrease fontSize="small" />
            </IconButton>
            <IconButton sx={{ p: 0, m: 0, minWidth: 0, }}
              onClick={() => editor.chain().focus().outdent().run()}>
              <IconIndentDescrease fontSize="small" />
            </IconButton>

            &nbsp;&nbsp;<button
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className="tiptapbutton"
              style={{ color: editorState.isHeading1 ? colorActive : colorInactive, marginRight: '5px' }}

            >
              H1
            </button>
            <button
              className="tiptapbutton"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              style={{ color: editorState.isHeading2 ? colorActive : colorInactive, marginRight: '5px' }}
            >
              H2
            </button>
            <button
              className="tiptapbutton"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              style={{ color: editorState.isHeading3 ? colorActive : colorInactive, marginRight: '5px' }}
            >
              H3
            </button>
            &nbsp;&nbsp; <button
              className="tiptapbutton" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
              Line
            </button>

            &nbsp;&nbsp;<button
              className="tiptapbutton" disabled={editorState.isLinkActive} style={{ marginRight: '5px' }}
              onClick={() => {
                const url = prompt('Enter URL')
                if (url) {
                  editor.commands.setLink({ href: url, target: '_blank' })
                }
              }}
            >
              Add Link
            </button>
            <button
              className="tiptapbutton" onClick={() => editor.commands.unsetLink()}
              disabled={!editorState.isLinkActive}>Remove Link
            </button>
            &nbsp; &nbsp; <button
              className="tiptapbutton" onClick={() => editor.chain().focus().undo().run()}
              disabled={!editorState.canUndo} style={{ marginRight: '5px' }}>
              Undo
            </button>
            <button
              className="tiptapbutton" onClick={() => editor.chain().focus().redo().run()} disabled={!editorState.canRedo}>
              Redo
            </button>
          </Toolbar>
        </Paper>
      </div>
    )
  }

  return (
    <div>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className='tiptap-editor' />
    </div>
  )
})

export default Tiptap;